import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { updatePrice } from "../../services/pricemanagment";
import { Card } from "primereact/card";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

const toNet = (gross: number, vatRate: number) => ((gross ?? 0) / (1 + vatRate)).toFixed(2);

const DefaultPricesEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stationPrices, originalResponse, stationType } = location.state || {};

  useEffect(() => {
    if (!location.state || !stationPrices || !originalResponse || !stationType) {
      navigate("/default-prices", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vatRate = originalResponse?.vat != null ? originalResponse.vat / 100 : 0.19;

  const [updatedPrice, setUpdatedPrice] = useState({
    transactionFee: 0,
    energyPrice: 0,
    blockingFee: 0,
    blockingFeeCap: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (stationPrices) {
      setUpdatedPrice({
        transactionFee: stationPrices.transactionFee ?? 0,
        energyPrice: stationPrices.energyPrice ?? 0,
        blockingFee: stationPrices.blockingFee?.price ?? 0,
        blockingFeeCap: stationPrices.blockingFeeCap?.price ?? 0,
      });
    }
  }, [stationPrices]);

  const handlePriceChange = (field: string, value: number) => {
    setUpdatedPrice((prev) => ({ ...prev, [field]: value ?? 0 }));
  };

  const handleSubmit = async () => {
    if (!stationPrices || !originalResponse) return;
    setSaving(true);
    setError(null);
    try {
      const feeMinDuration = stationPrices.blockingFee?.minDuration;
      const feeMaxDuration = stationPrices.blockingFee?.maxDuration;
      const capMinDuration = stationPrices.blockingFeeCap?.minDuration;
      const vat = originalResponse.vat ?? 19;

      const feeRestrictions = {
        ...(feeMinDuration !== undefined && { min_duration: feeMinDuration }),
        ...(feeMaxDuration !== undefined && { max_duration: feeMaxDuration }),
      };
      const capRestrictions = {
        ...(capMinDuration !== undefined && { min_duration: capMinDuration }),
      };

      const priceData = {
        id: originalResponse.id || "default",
        country_code: "DE",
        party_id: "COC",
        currency: "EUR",
        type: "REGULAR",
        elements: [
          {
            price_components: [
              { type: "FLAT", price: parseFloat(toNet(updatedPrice.transactionFee, vatRate)), vat, step_size: 1 },
            ],
          },
          {
            price_components: [
              { type: "ENERGY", price: parseFloat(toNet(updatedPrice.energyPrice, vatRate)), vat, step_size: 1000 },
            ],
          },
          {
            price_components: [
              { type: "TIME", price: parseFloat(toNet(updatedPrice.blockingFee, vatRate)), vat, step_size: 60 },
            ],
            ...(Object.keys(feeRestrictions).length > 0 && { restrictions: feeRestrictions }),
          },
          {
            price_components: [
              { type: "FLAT", price: parseFloat(toNet(updatedPrice.blockingFeeCap, vatRate)), vat, step_size: 1 },
            ],
            ...(Object.keys(capRestrictions).length > 0 && { restrictions: capRestrictions }),
          },
        ],
        last_updated: new Date().toISOString(),
      };

      await updatePrice(priceData, originalResponse.id);
      navigate("/default-prices");
    } catch (err) {
      console.error("Error updating prices:", err);
      setError("Failed to save prices. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <Card>
        <div className="text-2xl font-semibold text-900 mb-1">
          Edit Default Prices{stationType ? ` — ${stationType}` : ""}
        </div>
        <div className="text-color-secondary text-sm mb-4">
          Enter prices including VAT ({Math.round(vatRate * 100)}%).
        </div>

        <Message
          severity="warn"
          className="mb-4 w-full"
          text="These prices apply to all stations not using a community price."
        />

        {error && (
          <Message severity="error" text={error} className="mb-3 w-full" />
        )}

        <div className="p-fluid grid formgrid">
          <PriceField
            id="transactionFee"
            label="Transaction Fee"
            unit="EUR"
            value={updatedPrice.transactionFee}
            net={toNet(updatedPrice.transactionFee, vatRate)}
            onChange={(v) => handlePriceChange("transactionFee", v)}
          />
          <PriceField
            id="energyPrice"
            label="Energy Price"
            unit="EUR / kWh"
            value={updatedPrice.energyPrice}
            net={toNet(updatedPrice.energyPrice, vatRate)}
            onChange={(v) => handlePriceChange("energyPrice", v)}
          />
          <PriceField
            id="blockingFee"
            label="Blocking Fee"
            unit="EUR / min"
            value={updatedPrice.blockingFee}
            net={toNet(updatedPrice.blockingFee, vatRate)}
            onChange={(v) => handlePriceChange("blockingFee", v)}
          />
          <PriceField
            id="blockingFeeCap"
            label="Blocking Fee Cap"
            unit="EUR"
            value={updatedPrice.blockingFeeCap}
            net={toNet(updatedPrice.blockingFeeCap, vatRate)}
            onChange={(v) => handlePriceChange("blockingFeeCap", v)}
          />
        </div>

        <div className="flex gap-2 justify-content-end mt-4">
          <Button
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            outlined
            onClick={() => navigate("/default-prices")}
            disabled={saving}
          />
          <Button
            label="Save Changes"
            icon="pi pi-save"
            loading={saving}
            onClick={handleSubmit}
          />
        </div>
      </Card>
    </div>
  );
};

const PriceField: React.FC<{
  id: string;
  label: string;
  unit: string;
  value: number;
  net: string;
  onChange: (v: number) => void;
}> = ({ id, label, unit, value, net, onChange }) => (
  <div className="field col-12 md:col-6">
    <label htmlFor={id} className="font-medium">
      {label}{" "}
      <span className="text-color-secondary font-normal">
        ({unit}, incl. VAT)
      </span>
    </label>
    <InputNumber
      inputId={id}
      value={value}
      onValueChange={(e) => onChange(e.value)}
      mode="decimal"
      minFractionDigits={2}
      maxFractionDigits={2}
      min={0}
      placeholder="0.00"
    />
    <small className="text-color-secondary">
      Excl. VAT: <strong>{net} EUR</strong>
    </small>
  </div>
);

export default DefaultPricesEdit;
